import {
  evaluateMarker,
  probeMarkerImage,
} from 'trace-viewer/image-utils';
import type {
  ImageProbeReport,
  MarkerVerdict,
} from 'trace-viewer/image-utils';
import { resetDisclosures, wireDisclosures, wireFileInput } from '../shared/ui';
import { analyzeViaWorker } from './analyze-via-worker';
import { drawKeypointOverlay } from './keypoint-overlay';
import { renderApp } from './render';

export interface UiState {
  file: File | null;
  previewUrl: string | null;
  /**
   * If the image was loaded from a URL (via `?src=`), the original URL string
   * — surfaced in the UI so the user can see (and copy) where it came from.
   * null when the file came from a drop or file picker.
   */
  srcUrl: string | null;
  report: ImageProbeReport | null;
  /**
   * 'fetching' covers loading an image from `?src=`. 'loading-cv' covers the
   * first run while the worker is downloading opencv. 'analyzing' covers the
   * actual feature pass. After the first successful analyze, subsequent runs
   * go straight to 'analyzing'.
   */
  phase: 'idle' | 'fetching' | 'loading-cv' | 'analyzing' | 'done' | 'error';
  error: string | null;
  /** Flips to true after the first successful analysis. */
  cvReady: boolean;
  /** Whether to render the ORB keypoint overlay on the preview. */
  showKeypoints: boolean;
}

const initialState: UiState = {
  file: null,
  previewUrl: null,
  srcUrl: null,
  report: null,
  phase: 'idle',
  error: null,
  cvReady: false,
  showKeypoints: true,
};

let state: UiState = { ...initialState };

const root = document.getElementById('app')!;

function setState(patch: Partial<UiState>): void {
  state = { ...state, ...patch };
  render();
}

function currentVerdict(): MarkerVerdict | null {
  if (!state.report) return null;
  // No point showing a verdict if analysis bailed out — the report is mostly empty.
  if (state.report.bailout) return null;
  if (!state.report.features) return null;
  return evaluateMarker(state.report);
}

function render(): void {
  renderApp(root, { state, verdict: currentVerdict() });
  applyKeypointOverlay();
}

/**
 * Find the preview <img> + overlay <canvas> after each render and draw the
 * ORB keypoints. The image may not have loaded yet (especially on first
 * drop), so wire `onload` if we hit that case. Also redraw on window resize
 * because the canvas size tracks the displayed image size.
 */
function applyKeypointOverlay(): void {
  const img = root.querySelector<HTMLImageElement>('.marker-preview');
  const canvas = root.querySelector<HTMLCanvasElement>('.keypoint-overlay');
  if (!img || !canvas) return;
  const keypoints = state.report?.features?.keypoints ?? [];
  if (!state.showKeypoints || keypoints.length === 0) {
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    return;
  }
  const draw = (): void => drawKeypointOverlay(canvas, img, keypoints);
  if (img.complete && img.naturalWidth > 0) {
    draw();
  } else {
    img.addEventListener('load', draw, { once: true });
  }
}

window.addEventListener('resize', () => applyKeypointOverlay());

function revokePreview(): void {
  if (state.previewUrl) {
    URL.revokeObjectURL(state.previewUrl);
  }
}

/** Drop `?src` so a reload doesn't swap a picked file back to the URL image. */
function clearSrcParam(): void {
  if (!new URLSearchParams(window.location.search).has('src')) return;
  const next = new URL(window.location.href);
  next.searchParams.delete('src');
  window.history.replaceState(null, '', next.toString());
}

async function handleFile(file: File, srcUrl: string | null = null): Promise<void> {
  revokePreview();
  if (!srcUrl) clearSrcParam();
  resetDisclosures();
  const previewUrl = URL.createObjectURL(file);
  setState({
    file,
    previewUrl,
    srcUrl,
    report: null,
    phase: state.cvReady ? 'analyzing' : 'loading-cv',
    error: null,
  });

  try {
    const report = await probeMarkerImage(file, {
      analyze: analyzeViaWorker,
      onDecoded: (partial) => {
        setState({
          report: partial,
          phase: state.cvReady ? 'analyzing' : 'loading-cv',
        });
      },
    });
    setState({
      report,
      phase: 'done',
      cvReady: state.cvReady || report.bailout === null,
    });
  } catch (err) {
    setState({ phase: 'error', error: (err as Error).message ?? 'Analysis failed' });
  }
}

/**
 * Fetch an image from a URL and run it through the analyzer.
 *
 * Cross-origin sources need to grant CORS — failures are reported in the UI
 * rather than thrown, so a bad ?src= doesn't leave the page stuck.
 */
async function loadFromUrl(rawUrl: string): Promise<void> {
  let url: URL;
  try {
    url = new URL(rawUrl, window.location.href);
  } catch {
    setState({ phase: 'error', error: `The ?src address isn’t a valid URL: ${rawUrl}` });
    return;
  }

  setState({ phase: 'fetching', srcUrl: url.href, error: null });

  let blob: Blob;
  try {
    const response = await fetch(url.href, { mode: 'cors', credentials: 'omit' });
    if (!response.ok) {
      setState({ phase: 'error', error: `The server answered HTTP ${response.status} ${response.statusText}.` });
      return;
    }
    blob = await response.blob();
  } catch (err) {
    // Cross-origin failures, network errors, COEP rejection all surface as
    // TypeError. The message varies by browser — keep it actionable.
    const detail = err instanceof Error ? err.message : String(err);
    setState({
      phase: 'error',
      error: `Couldn’t download ${url.href} (${detail}). The server may not allow other sites to read it — download the image and drop it here instead.`,
    });
    return;
  }

  if (!blob.type.startsWith('image/')) {
    setState({ phase: 'error', error: `That address returned ${blob.type || 'something unknown'}, not an image.` });
    return;
  }

  // Derive a filename from the URL path so the UI has something to label it
  // with. Fall back to "marker" if the path has no useful tail.
  const name = url.pathname.split('/').filter(Boolean).pop() || 'marker';
  const file = new File([blob], name, { type: blob.type });
  await handleFile(file, url.href);
}

root.addEventListener('change', (e) => {
  const target = e.target as HTMLElement;
  if (target.matches('input[data-action="toggle-keypoints"]')) {
    setState({ showKeypoints: (target as HTMLInputElement).checked });
  }
});

wireFileInput(root, (file) => void handleFile(file));
wireDisclosures(root);

render();

// If the page was opened with `?src=<url>`, fetch that image and feed it
// straight into the analyzer. Useful for sharing analyses ("look at how this
// marker scores") and for testing.
const srcParam = new URLSearchParams(window.location.search).get('src');
if (srcParam) {
  void loadFromUrl(srcParam);
}

// We do NOT warm opencv.js on page load. The script is ~10 MB and parsing it
// blocks the main thread for several seconds on typical machines, which
// makes the page feel frozen right after the layout paints. opencv loads
// lazily on the first file drop instead — the "Loading analyzer…" status
// makes the cost visible at a point where the user is already waiting on
// their result.
